import { useState, useEffect, useCallback, useMemo } from 'react';
import { FiBell, FiLock, FiAlertTriangle, FiFlag, FiAlertOctagon } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Card from '../components/common/Card';
import EmptyState from '../components/common/EmptyState';
import SegmentedControl from '../components/common/SegmentedControl';
import Button from '../components/common/Button';
import { notificationAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { useUI } from '../context/UIContext';

// The four security-alert notification types fanned out by
// backend/services/adminAlertService.js. Filter keys double as the
// server-side `type` query values.
const ALERT_TYPES = {
  admin_alert_account_locked: { label: 'Lockouts', icon: FiLock, tone: '#8a6a1f' },
  admin_alert_user_compromised: { label: 'Compromised', icon: FiAlertOctagon, tone: '#9a3a3a' },
  admin_alert_new_flag: { label: 'Flags', icon: FiFlag, tone: '#4a6b3f' },
  admin_alert_critical_error: { label: 'Critical errors', icon: FiAlertTriangle, tone: '#9a564a' },
};
const ALL_TYPES = Object.keys(ALERT_TYPES).join(',');

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// Admin Alerts — super_admin-only feed of automated security alerts
// (account lockouts, compromised accounts, new flags, critical errors).
// A filtered view over the Notification inbox; the same rows also land in
// the regular notification bell.
export default function AdminAlertsPage() {
  const { showToast } = useUI();
  const [typeFilter, setTypeFilter] = useState('all');
  // Stale-while-revalidate: this route unmounts on nav, so page 1 is cached
  // module-side (services/viewCache) and rendered instantly on return. On mount
  // the filter is 'all', so the seeds read that default key. Keyed by filter.
  const [alerts, setAlerts] = useState(() => getView(`admin:alerts:${ALL_TYPES}`)?.alerts ?? []);
  const [pagination, setPagination] = useState(() => getView(`admin:alerts:${ALL_TYPES}`)?.pagination ?? {});
  const [loading, setLoading] = useState(() => !hasView(`admin:alerts:${ALL_TYPES}`));

  const typeParam = typeFilter === 'all' ? ALL_TYPES : typeFilter;

  const fetchAlerts = useCallback(async (page = 1) => {
    const key = `admin:alerts:${typeParam}`;
    // Page 1 gets stale-while-revalidate — a full gate only on a true first
    // load. A "Load more" (page > 1) always shows its inline button spinner.
    if (page > 1 || !hasView(key)) setLoading(true);
    try {
      const res = await notificationAPI.getNotifications({ type: typeParam, page, limit: 25 });
      if (res.success) {
        const rows = res.data?.notifications || [];
        const pg = res.data?.pagination || {};
        if (page === 1) {
          setAlerts(rows);
          setView(key, { alerts: rows, pagination: pg });
        } else {
          setAlerts((prev) => [...prev, ...rows]);
        }
        setPagination(pg);
      }
    } catch (err) {
      // Keep the cached page on a failed refresh; only toast on a first load.
      console.error('Error fetching admin alerts:', err);
      if (!hasView(key)) showToast('Failed to load admin alerts', 'error');
    } finally {
      setLoading(false);
    }
  }, [typeParam, showToast]);

  useEffect(() => { fetchAlerts(1); }, [fetchAlerts]);

  const markRead = async (alert) => {
    if (alert.read) return;
    try {
      await notificationAPI.markAsRead(alert._id);
      setAlerts((prev) => prev.map((a) => (a._id === alert._id ? { ...a, read: true } : a)));
    } catch (_) { /* read state is cosmetic here */ }
  };

  const filterOptions = useMemo(() => ([
    { key: 'all', label: 'All' },
    ...Object.entries(ALERT_TYPES).map(([key, t]) => ({ key, label: t.label })),
  ]), []);

  const hasMore = (pagination.page || 1) < (pagination.pages || 1);

  return (
    <AppLayout>
      <PageHeader
        title="Admin Alerts"
        subtitle="Automated security alerts — lockouts, compromised accounts, user flags, and critical errors"
        actions={(
          <SegmentedControl options={filterOptions} value={typeFilter} onChange={setTypeFilter} />
        )}
      />

      {!loading && alerts.length === 0 ? (
        <EmptyState
          icon={FiBell}
          tone="#145f5f"
          title={typeFilter === 'all' ? 'No security alerts' : 'No alerts of this type'}
          subtitle={typeFilter === 'all'
            ? 'Automated alerts appear here the moment a security event fires.'
            : 'Try another category, or clear the filter.'}
          variant={typeFilter === 'all' ? 'default' : 'filter'}
          action={typeFilter !== 'all' && (
            <Button variant="outline" size="sm" onClick={() => setTypeFilter('all')}>Show all</Button>
          )}
        />
      ) : (
        <div className="flex flex-col gap-2">
          {alerts.map((alert) => {
            const meta = ALERT_TYPES[alert.type] || { label: 'Alert', icon: FiBell, tone: '#145f5f' };
            const Ico = meta.icon;
            return (
              <Card
                key={alert._id}
                hover
                as="button"
                onClick={() => markRead(alert)}
                className="text-left w-full flex items-start gap-3"
                style={alert.read ? { opacity: 0.72 } : undefined}
              >
                <div
                  className="shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center"
                  style={{ background: `${meta.tone}14`, color: meta.tone }}
                >
                  <Ico size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-bold text-[#08111f]">{alert.title}</span>
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider px-2 py-[2px] rounded-full"
                      style={{ background: `${meta.tone}14`, color: meta.tone }}
                    >
                      {meta.label}
                    </span>
                    {alert.priority === 'urgent' && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-[2px] rounded-full bg-[#9a3a3a14] text-[#9a3a3a]">
                        Urgent
                      </span>
                    )}
                    {!alert.read && <span className="w-2 h-2 rounded-full bg-[#145f5f] shrink-0" />}
                  </div>
                  <div className="text-[12.5px] text-[#4a4436] mt-[3px] break-words">{alert.message}</div>
                  <div className="text-[11px] text-[#7a7060] mt-1">{timeAgo(alert.createdAt)}</div>
                </div>
              </Card>
            );
          })}

          {hasMore && (
            <div className="flex justify-center mt-2">
              <Button variant="outline" size="sm" disabled={loading} onClick={() => fetchAlerts((pagination.page || 1) + 1)}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
